<template>
  <AkModal
    ref="modal"
    size="lg"
    :close-on-backdrop="false"
    data-testid="german-bi-modal"
  >
    <Transition
      name="state-fade"
      mode="out-in"
    >
      <div
        v-if="!isSubmitted"
        key="form"
        class="german-bi-modal"
      >
        <AkParagraph
          weight="bold"
          size="lg"
          class="german-bi-modal__title"
        >
          {{ t('retailer.germanBusinessIdentifier.modal.title') }}
        </AkParagraph>

        <AkParagraph class="german-bi-modal__subtitle">
          {{ t('retailer.germanBusinessIdentifier.modal.subtitle') }}
        </AkParagraph>

        <GermanBiForm
          ref="germanBiForm"
          @submitted="onFormSubmitted"
        />
      </div>

      <div
        v-else
        key="success"
        class="german-bi-modal german-bi-modal--success"
        data-testid="success-state"
      >
        <div class="german-bi-modal__success-icon">
          <AkIcon
            symbol="check"
            size="md"
          />
        </div>

        <AkParagraph
          weight="bold"
          size="lg"
          class="german-bi-modal__success-title"
        >
          {{ t('retailer.germanBusinessIdentifier.modal.successTitle') }}
        </AkParagraph>

        <AkParagraph class="german-bi-modal__success-subtitle">
          <i18n-t keypath="retailer.germanBusinessIdentifier.modal.successSubtitle">
            <template #settings>
              <strong>{{ t('Settings') }}</strong>
            </template>
          </i18n-t>
        </AkParagraph>

        <AkButton
          data-testid="success-cta-button"
          @click="close"
        >
          {{ t('retailer.germanBusinessIdentifier.modal.successCta') }}
        </AkButton>
      </div>
    </Transition>
  </AkModal>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { AkModal, AkParagraph, AkButton, AkIcon } from '@ankorstore/design-system';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { trackModalViewed, trackModalClosed } from '../composables/use-german-bi';
import GermanBiForm from './german-bi-form.vue';

const { t } = useI18n();
const route = useRoute();

const emit = defineEmits<{
  submitted: [];
}>();

const modal = ref<InstanceType<typeof AkModal> | null>(null);
const germanBiForm = ref<InstanceType<typeof GermanBiForm> | null>(null);
const isSubmitted = ref(false);

function open(): void {
  isSubmitted.value = false;
  germanBiForm.value?.reset();
  modal.value!.openModal();
  trackModalViewed(route.path);
}

function close(): void {
  modal.value!.close();
  trackModalClosed();
}

function onFormSubmitted(): void {
  emit('submitted');
  isSubmitted.value = true;
}

defineExpose({ open, close });
</script>

<style scoped lang="scss">
.german-bi-modal {
  @apply ds-flex ds-flex-col ds-p-4 ds-text-left;

  &--success {
    @apply ds-text-center ds-items-center ds-py-8;
  }

  &__title {
    @apply ds-mb-2;
  }

  &__subtitle {
    @apply ds-mb-6 ds-text-neutral-700;
  }

  &__success-icon {
    @apply ds-w-7 ds-h-7 ds-rounded-full ds-flex ds-items-center ds-justify-center ds-mx-auto ds-mb-4 ds-text-primary ds-bg-success-500;
  }

  &__success-title {
    @apply ds-mb-2;
  }

  &__success-subtitle {
    @apply ds-mb-6 ds-text-neutral-700;
  }
}

.state-fade-enter-active,
.state-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.state-fade-enter-from {
  opacity: 0;
  transform: scale(0.96);
}

.state-fade-leave-to {
  opacity: 0;
  transform: scale(1.02);
}
</style>
